import { api } from '@/lib/api';
import { notify } from '@/components/ui/ConfirmHost';
import { ReinstateModal } from './StudentsPage';
import { askConfirm, askPrompt } from '@/components/ui/ConfirmHost';
import React, { useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useBase } from '@/lib/useBase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getStudentById, putStudent, withdrawStudent, reinstateStudent,
  transferStudentClass, getClasses, getGuardians, linkGuardianToStudent,
  deleteGuardianStudent, uploadStudentDocument, getStudentPaymentReference,
  getInvoices, getPayments, getClassResults, getAttendance, getTerms,
  deleteStudentDocument, requestStudentDocument,
  getStudentDocumentRequests,
  acceptStudentDocumentRequest,
  declineStudentDocumentRequest,
} from '@/api/owner';
import {
  ArrowLeft, Edit2, UserPlus, X, Upload, FileText, ExternalLink,
  UserMinus, UserCheck, Wallet, ArrowRightLeft, Copy, Receipt,
  CheckCircle2, Clock, AlertCircle, Trash2,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const money = (n?: number | null) => n == null ? '—' : '₦' + Number(n).toLocaleString('en-NG');
const dateOnly = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const RELATIONSHIPS = ['father', 'mother', 'guardian', 'other'];

function passportUrl(student: any): string | null {
  const docs = arr<any>(student?.documents);
  if (!docs.length) return null;
  const byType = docs.find((d: any) => String(d.type ?? '').toLowerCase().includes('passport'));
  if (byType?.url ?? byType?.file_url) return byType.url ?? byType.file_url;
  const isImage = (url?: string | null) => !!url && /\.(png|jpe?g|webp|gif)$/i.test(url);
  const image = docs.find((d: any) => isImage(d.url) || isImage(d.file_url));
  return image?.url ?? image?.file_url ?? null;
}

type Tab = 'overview' | 'parents' | 'fees' | 'results' | 'attendance' | 'documents';

export function StudentProfilePage() {
  const { id } = useParams();
  const studentId = Number(id);
  const navigate = useNavigate();
  const base = useBase();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const studentQ = useQuery({ queryKey: ['owner-student', studentId], queryFn: () => getStudentById(studentId).then((r: any) => r.data?.data ?? r.data) });
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses().then((r: any) => r.data?.data ?? r.data) });
  const guardiansQ = useQuery({ queryKey: ['owner-guardians'], queryFn: () => getGuardians().then((r: any) => r.data?.data ?? r.data) });
  const termsQ = useQuery({ queryKey: ['owner-terms'], queryFn: () => getTerms().then((r: any) => r.data?.data ?? r.data) });
  const paymentRefQ = useQuery({ queryKey: ['owner-student-ref', studentId], queryFn: () => getStudentPaymentReference(studentId).then((r: any) => r.data?.data ?? r.data), retry: false });
  const invoicesQ = useQuery({ queryKey: ['owner-student-invoices', studentId], queryFn: () => getInvoices({ student_id: studentId }).then((r: any) => r.data?.data ?? r.data), retry: false });
  const paymentsQ = useQuery({ queryKey: ['owner-student-payments', studentId], queryFn: () => getPayments({ student_id: studentId }).then((r: any) => r.data?.data ?? r.data), retry: false });
  const currentTerm = termsQ.data ? (arr<any>(termsQ.data).find((t: any) => t.is_current) ?? arr<any>(termsQ.data)[0]) : null;
  const studentClassId = (studentQ.data as any)?.current_class_id ?? (studentQ.data as any)?.student?.current_class_id;

  const resultsQ = useQuery({
    queryKey: ['owner-student-results', studentId, studentClassId, currentTerm?.id],
    queryFn: () => {
      if (!studentClassId || !currentTerm?.id) return Promise.resolve(null);
      return getClassResults(studentClassId, { term_id: currentTerm.id }).then((r: any) => r.data?.data ?? r.data);
    },
    enabled: !!studentClassId && !!currentTerm?.id,
    retry: false,
  });
  const attendanceQ = useQuery({
    queryKey: ['owner-student-attendance', studentId, studentClassId],
    queryFn: () => {
      if (!studentClassId) return Promise.resolve(null);
      return getAttendance({ class_id: studentClassId }).then((r: any) => {
        const d = r.data?.data ?? r.data;
        const entries = Array.isArray(d) ? d : (d?.entries ?? d?.data ?? []);
        return { entries: entries.filter((e: any) => Number(e.student_id) === studentId) };
      });
    },
    enabled: !!studentClassId,
    retry: false,
  });

  const raw = studentQ.data;
  const student = raw?.student ?? raw?.data?.student ?? raw;
  const classes = arr<any>(classesQ.data);
  const guardians = arr<any>(guardiansQ.data);
  const terms = arr<any>(termsQ.data);
  const paymentRef = paymentRefQ.data as any;
  const invoices = arr<any>(invoicesQ.data);
  const payments = arr<any>(paymentsQ.data);
  const allResults = arr<any>(resultsQ.data?.results ?? resultsQ.data);
  const termResult = allResults.find((r: any) => Number(r.student_id) === studentId);
  // the per-subject breakdown of this student's term result (subject names, each column, total, grade)
  const sheetQ = useQuery({
    queryKey: ['owner-student-result-sheet', termResult?.result_id],
    queryFn: () => api.get(`/school/results/${termResult.result_id}/sheet`).then((r: any) => r.data?.data ?? r.data),
    enabled: !!termResult?.result_id, retry: false,
  });
  const sheet = sheetQ.data as any;
  const studentResults = arr<any>(sheet?.subjects);
  const attendance = attendanceQ.data as any;
  const attendanceArr = arr<any>(attendanceQ.data?.entries ?? attendanceQ.data);
  const documents = arr<any>(student?.documents ?? raw?.documents);
  const guardiansList = arr<any>(student?.guardians);

  const totalOutstanding = invoices.reduce((s: number, i: any) => s + Number(i.balance ?? 0), 0);
  const totalPaid = invoices.reduce((s: number, i: any) => s + Number(i.amount_paid ?? 0), 0);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['owner-student', studentId] });
    qc.invalidateQueries({ queryKey: ['owner-student-invoices', studentId] });
    qc.invalidateQueries({ queryKey: ['owner-student-payments', studentId] });
    setEditOpen(false);
    setWithdrawOpen(false);
    setLinkOpen(false);
    setTransferOpen(false);
  };

  const updateM = useMutation({ mutationFn: (b: any) => putStudent(studentId, b), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const withdrawM = useMutation({ mutationFn: (b: any) => withdrawStudent(studentId, b), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const [reinstateOpen, setReinstateOpen] = useState(false);
  const reinstateM = useMutation({
    mutationFn: (b: { school_class_id: number; password?: string }) => reinstateStudent(studentId, b),
    onSuccess: (r: any) => {
      setReinstateOpen(false); refresh();
      const login = r.data?.data?.login;
      void notify(login ? `They can sign in now.\nAdmission number: ${login.admission_no}\nPassword: ${login.password}` : 'The student is active again.', { title: 'Student is active again', tone: 'success' });
    },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Failed'),
  });
  const transferM = useMutation({ mutationFn: (b: any) => transferStudentClass(studentId, b), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const linkM = useMutation({ mutationFn: (d: any) => linkGuardianToStudent(d.guardianId, d.body), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const unlinkM = useMutation({ mutationFn: (guardianId: number) => deleteGuardianStudent(guardianId, studentId), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const uploadM = useMutation({ mutationFn: (fd: FormData) => uploadStudentDocument(studentId, fd), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });

  const deleteDocM = useMutation({
    mutationFn: (docId: number) => deleteStudentDocument(studentId, docId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner-student', studentId] }),
    onError: (e: any) => alert(e.response?.data?.message ?? 'Failed'),
  });
  const requestDocM = useMutation({
    mutationFn: (d: { type: string; note?: string }) => requestStudentDocument(studentId, d),
    onSuccess: (r: any) => { alert(r.data?.message ?? 'Request sent to parent'); setRequestOpen(false); },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Failed'),
  });

  const docRequestsQ = useQuery({
    queryKey: ['owner-student-doc-requests', studentId],
    queryFn: () => getStudentDocumentRequests(studentId).then((r: any) => r.data?.data ?? r.data),
    enabled: !!studentId && tab === 'documents',
  });
  const docRequests = arr<any>(docRequestsQ.data);

  const acceptReqM = useMutation({
    mutationFn: (id: number) => acceptStudentDocumentRequest(studentId, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['owner-student-doc-requests', studentId] }); refresh(); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Accept failed'),
  });

  const declineReqM = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => declineStudentDocumentRequest(studentId, id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner-student-doc-requests', studentId] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Decline failed'),
  });

  const saveProfileM = useMutation({
    mutationFn: async ({ data, passport }: { data: any; passport?: File }) => {
      await putStudent(studentId, data);
      if (passport) {
        const fd = new FormData();
        fd.append('type', 'passport');
        fd.append('file', passport);
        await uploadStudentDocument(studentId, fd);
      }
    },
    onSuccess: () => { refresh(); setEditOpen(false); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Save failed'),
  });

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('type', 'Student Document');
    fd.append('file', f);
    uploadM.mutate(fd);
    e.target.value = '';
  };

  const copyRef = () => {
    const ref = paymentRef?.reference ?? paymentRef?.payment_reference ?? '';
    if (!ref) return;
    navigator.clipboard?.writeText(String(ref));
    alert('Payment reference copied: ' + ref);
  };

  if (studentQ.isLoading) return <div className="p-8 text-center text-slate-500">Loading student…</div>;
  if (!student) return <div className="p-8 text-center text-slate-500">Student not found.</div>;

  const fullName = `${student.first_name ?? ''} ${student.middle_name ?? ''} ${student.last_name ?? ''}`.replace(/\s+/g, ' ').trim();
  const className = student.class ?? student.current_class?.name ?? '—';

  const TABS: { key: Tab; label: string; badge?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'parents', label: 'Parents', badge: guardiansList.length },
    { key: 'fees', label: 'Fees', badge: invoices.length },
    { key: 'results', label: 'Results', badge: studentResults.length },
    { key: 'attendance', label: 'Attendance' },
    { key: 'documents', label: 'Documents', badge: documents.length },
  ];

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-10 p-6">

      {/* HEADER */}
      <div>
        <button onClick={() => navigate(`${base}/students`)} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-2">
          <ArrowLeft className="w-4 h-4" /> Back to Students
        </button>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div className="flex items-center gap-4">
            {passportUrl(student) ? (
              <img src={passportUrl(student)!} alt={fullName} className="w-16 h-16 rounded-xl object-cover border border-slate-200 flex-shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-slate-200 flex items-center justify-center text-xl font-extrabold text-slate-600 flex-shrink-0">
                {(student.first_name?.[0] ?? '') + (student.last_name?.[0] ?? '')}
              </div>
            )}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">STUDENT · {student.admission_no ?? '—'}</p>
              <h1 className="text-3xl font-extrabold text-slate-900">{fullName}</h1>
              <p className="text-slate-500 text-sm mt-1">{className} · <span className={`font-bold ${student.status === 'active' ? 'text-green-700' : 'text-red-700'}`}>{student.status ?? 'active'}</span></p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setTransferOpen(true)} className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg font-semibold text-sm shadow-sm hover:bg-slate-50 flex items-center gap-1">
              <ArrowRightLeft className="w-4 h-4" /> Transfer
            </button>
            {student.status === 'active' ? (
              <button onClick={() => setWithdrawOpen(true)} className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg font-semibold text-sm shadow-sm hover:bg-red-100 flex items-center gap-1">
                <UserMinus className="w-4 h-4" /> Withdraw
              </button>
            ) : student.status === 'withdrawn' ? (
              <button onClick={() => setReinstateOpen(true)} className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg font-semibold text-sm shadow-sm hover:bg-green-100 flex items-center gap-1">
                <UserCheck className="w-4 h-4" /> Reinstate
              </button>
            ) : null}
            <button onClick={() => setEditOpen(true)} className="bg-[#0f172a] hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-sm flex items-center gap-2">
              <Edit2 className="w-4 h-4" /> Edit profile
            </button>
          </div>
        </div>
      </div>

      {/* QUICK STATS ROW */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickStat label="Outstanding fees" value={money(totalOutstanding)} tone={totalOutstanding > 0 ? 'text-red-700' : 'text-green-700'} />
        <QuickStat label="Paid to date" value={money(totalPaid)} tone="text-slate-900" />
        <QuickStat label="Payment reference" value={paymentRef?.reference ?? paymentRef?.payment_reference ?? '—'} action={<button onClick={copyRef} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>} />
        <QuickStat label="Attendance" value={attendance?.percentage != null ? `${Number(attendance.percentage).toFixed(0)}%` : (attendanceArr.length ? '—' : '—')} tone="text-slate-900" />
      </div>

      {/* TABS */}
      <div className="flex gap-2 flex-wrap border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`pb-3 px-3 text-sm font-bold border-b-2 flex items-center gap-1.5 ${tab === t.key ? 'border-[#0f172a] text-[#0f172a]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {t.label}
            {t.badge != null && t.badge > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded-full">{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Personal details">
            <Field label="Date of birth" value={dateOnly(student.date_of_birth)} />
            <Field label="Gender" value={student.gender ?? '—'} capitalize />
            <Field label="Blood group" value={student.blood_group ?? '—'} />
            <Field label="Admitted on" value={dateOnly(student.admitted_on)} />
            <Field label="Home address" value={student.address ?? '—'} />
            <Field label="Previous school" value={student.previous_school ?? '—'} />
            <Field label="Medical notes" value={student.medical_notes ?? '—'} />
          </Card>
          <Card title="Guardians">
            {guardiansList.length === 0 ? <p className="text-sm text-slate-500">No guardian linked yet.</p> :
              <ul className="divide-y divide-slate-100">
                {guardiansList.map((g: any) => {
                  const isPrimary = g.is_primary === 1 || g.is_primary === true || g.pivot?.is_primary === 1 || g.pivot?.is_primary === true;
                  return (
                    <li key={g.id} className="py-2 flex justify-between">
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">{g.first_name} {g.last_name}</div>
                        <div className="text-xs text-slate-500">{g.phone} · {g.relationship ?? g.pivot?.relationship ?? '—'}</div>
                      </div>
                      {isPrimary && <span className="text-[10px] font-bold px-2 py-0.5 bg-green-100 text-green-700 rounded self-start">PRIMARY</span>}
                    </li>
                  );
                })}
              </ul>
            }
            <button onClick={() => setLinkOpen(true)} className="mt-3 flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg font-semibold text-xs">
              <UserPlus className="w-3.5 h-3.5" /> Link parent
            </button>
          </Card>
        </div>
      )}

      {/* PARENTS TAB */}
      {tab === 'parents' && (
        <Card title="Parents & guardians" action={<button onClick={() => setLinkOpen(true)} className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Link parent</button>}>
          {guardiansList.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No parents linked yet.</p> :
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider">
                <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Relationship</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {guardiansList.map((g: any) => {
                  const isPrimary = g.is_primary === 1 || g.is_primary === true || g.pivot?.is_primary === 1 || g.pivot?.is_primary === true;
                  return (
                    <tr key={g.id}>
                      <td className="px-3 py-2 font-semibold">{g.first_name} {g.last_name}</td>
                      <td className="px-3 py-2 text-xs capitalize">{g.relationship ?? g.pivot?.relationship ?? '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{g.phone}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${isPrimary ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {isPrimary ? 'Primary payer' : 'Linked'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={async () => { if (await askConfirm(`Unlink ${g.first_name} ${g.last_name} from this student?`)) unlinkM.mutate(g.id); }} className="text-xs font-semibold text-red-600 hover:underline">Unlink</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          }
        </Card>
      )}

      {/* FEES TAB */}
      {tab === 'fees' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total invoiced</p>
              <p className="text-2xl font-extrabold text-slate-900">{money(invoices.reduce((s: number, i: any) => s + Number(i.total ?? 0), 0))}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Outstanding</p>
              <p className={`text-2xl font-extrabold ${totalOutstanding > 0 ? 'text-red-700' : 'text-green-700'}`}>{money(totalOutstanding)}</p>
            </div>
          </div>

          <Card title="Invoices">
            {invoicesQ.isLoading ? <p className="text-sm text-slate-500">Loading…</p> :
             invoices.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No invoices yet.</p> :
             <table className="w-full text-left text-sm">
               <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider">
                 <tr><th className="px-3 py-2">Invoice</th><th className="px-3 py-2">Term</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2">Status</th></tr>
               </thead>
               <tbody className="divide-y divide-slate-100">
                 {invoices.map((inv: any) => (
                   <tr key={inv.id}>
                     <td className="px-3 py-2 font-mono text-xs">{inv.number}</td>
                     <td className="px-3 py-2 text-xs">{inv.term_id ?? '—'}</td>
                     <td className="px-3 py-2 text-right font-mono">{money(inv.total)}</td>
                     <td className="px-3 py-2 text-right font-mono text-green-700">{money(inv.amount_paid)}</td>
                     <td className="px-3 py-2 text-right font-mono text-red-700 font-bold">{money(inv.balance)}</td>
                     <td className="px-3 py-2">
                       <span className={`text-xs font-bold px-2 py-0.5 rounded ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{inv.status}</span>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
            }
          </Card>

          <Card title="Payment history">
            {paymentsQ.isLoading ? <p className="text-sm text-slate-500">Loading…</p> :
             payments.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No payments recorded yet.</p> :
             <table className="w-full text-left text-sm">
               <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider">
                 <tr><th className="px-3 py-2">Reference</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Method</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Status</th></tr>
               </thead>
               <tbody className="divide-y divide-slate-100">
                 {payments.map((p: any) => (
                   <tr key={p.id}>
                     <td className="px-3 py-2 font-mono text-xs">{p.reference ?? '—'}</td>
                     <td className="px-3 py-2 text-xs">{dateOnly(p.paid_at ?? p.created_at)}</td>
                     <td className="px-3 py-2 text-xs capitalize">{p.method ?? '—'}</td>
                     <td className="px-3 py-2 text-right font-mono text-green-700">{money(p.amount)}</td>
                     <td className="px-3 py-2"><span className="text-xs font-bold px-2 py-0.5 bg-green-100 text-green-700 rounded">{p.status ?? 'successful'}</span></td>
                   </tr>
                 ))}
               </tbody>
             </table>
            }
          </Card>
        </div>
      )}

      {/* RESULTS TAB */}
      {tab === 'results' && (
        <Card title={`Results${sheet ? ` · ${sheet.term} · ${sheet.class}` : ''}`}>
          {resultsQ.isLoading || sheetQ.isLoading ? <p className="text-sm text-slate-500">Loading…</p> :
           !termResult ? <p className="text-sm text-slate-500 py-6 text-center">No computed results for this student this term yet. Compute results from the Results page.</p> :
           <div className="space-y-4">
             <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
               {[['Total', sheet?.summary?.total ?? termResult.total], ['Average', sheet?.summary?.average ?? termResult.average], ['Grade', sheet?.summary?.grade ?? termResult.grade ?? '—'],
                 ['Position', (sheet?.summary?.position ?? termResult.position) ? `${sheet?.summary?.position ?? termResult.position}${sheet?.summary?.class_size ? ` of ${sheet.summary.class_size}` : ''}` : '—']].map(([k, v]) => (
                 <div key={k as string} className="rounded-xl bg-slate-50 px-3 py-2"><p className="text-[11px] uppercase font-bold text-slate-400">{k}</p><p className="font-bold text-slate-900">{v as any}</p></div>
               ))}
             </div>
             <div className="overflow-x-auto">
               <table className="w-full text-left text-sm">
                 <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider">
                   <tr><th className="px-3 py-2">Subject</th>
                     {arr<any>(sheet?.columns).map((c: any) => <th key={c.code} className="px-3 py-2 text-right">{c.code}<span className="block text-[10px] font-normal normal-case">/{c.max}</span></th>)}
                     <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Grade</th><th className="px-3 py-2 text-right">Position</th></tr>
                 </thead>
                 <tbody className="divide-y divide-slate-100">
                   {studentResults.map((r: any, i: number) => (
                     <tr key={i}>
                       <td className="px-3 py-2 font-semibold">{r.subject ?? '—'}</td>
                       {arr<any>(sheet?.columns).map((c: any) => <td key={c.code} className="px-3 py-2 text-right font-mono text-slate-600">{r.scores?.[c.code] ?? '—'}</td>)}
                       <td className="px-3 py-2 text-right font-mono font-bold">{r.total ?? '—'}</td>
                       <td className="px-3 py-2 text-right"><span className="text-xs font-bold px-2 py-0.5 bg-slate-100 text-slate-700 rounded">{r.grade ?? '—'}</span></td>
                       <td className="px-3 py-2 text-right text-slate-600">{r.position ?? '—'}</td>
                     </tr>
                   ))}
                   {!studentResults.length && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">{sheetQ.isError ? 'Could not load the subjects.' : 'No subjects on this result.'}</td></tr>}
                 </tbody>
               </table>
             </div>
           </div>
          }
        </Card>
      )}

      {/* ATTENDANCE TAB */}
      {tab === 'attendance' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 p-5 rounded-xl text-center">
            <p className="text-xs font-semibold text-green-700 uppercase">Present</p>
            <p className="text-4xl font-extrabold text-green-700 mt-1">{attendance?.present ?? 0}</p>
          </div>
          <div className="bg-red-50 border border-red-200 p-5 rounded-xl text-center">
            <p className="text-xs font-semibold text-red-700 uppercase">Absent</p>
            <p className="text-4xl font-extrabold text-red-700 mt-1">{attendance?.absent ?? 0}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-5 rounded-xl text-center">
            <p className="text-xs font-semibold text-amber-700 uppercase">Late</p>
            <p className="text-4xl font-extrabold text-amber-700 mt-1">{attendance?.late ?? 0}</p>
          </div>
        </div>
      )}

      {/* DOCUMENTS TAB */}
      {tab === 'documents' && (
        <div className="space-y-4">
        <Card title="Document requests">
          {docRequestsQ.isLoading ? <p className="text-sm text-slate-500 py-4">Loading…</p> :
           docRequests.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">No requests sent yet.</p> :
           <ul className="divide-y divide-slate-100">
             {docRequests.map((r: any) => (
               <li key={r.id} className="py-3">
                 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                   <div className="min-w-0">
                     <p className="text-sm font-semibold text-slate-900 capitalize flex items-center gap-2">
                       {r.document_type?.replace(/_/g, ' ')}
                       <span className={
                         'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ' +
                         (r.status === 'pending'   ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          r.status === 'responded' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          r.status === 'accepted'  ? 'bg-green-50 text-green-700 border-green-200' :
                                                     'bg-red-50 text-red-700 border-red-200')
                       }>{r.status}</span>
                     </p>
                     {r.note && <p className="text-xs text-slate-500 mt-0.5">Note: {r.note}</p>}
                     {r.response_note && <p className="text-xs text-slate-500 mt-0.5">Parent said: {r.response_note}</p>}
                     {r.declined_reason && <p className="text-xs text-red-600 mt-0.5">Declined: {r.declined_reason}</p>}
                     <p className="text-xs text-slate-400 mt-1">
                       Requested {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—'}
                       {r.responded_at && ' · Replied ' + new Date(r.responded_at).toLocaleDateString('en-GB')}
                     </p>
                   </div>
                   <div className="flex flex-wrap gap-2">
                     {r.document?.url && (
                       <a href={r.document.url} target="_blank" rel="noreferrer"
                         className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-1 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg">
                         <ExternalLink className="w-3 h-3" /> View file
                       </a>
                     )}
                     {r.status === 'responded' && (
                       <>
                         <button onClick={() => acceptReqM.mutate(r.id)} disabled={acceptReqM.isPending}
                           className="text-xs font-semibold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg disabled:opacity-50">
                           Accept
                         </button>
                         <button onClick={async () => { const reason = await askPrompt('The requester sees this reason.', { title: 'Decline this request', confirmLabel: 'Decline', placeholder: 'Reason for declining', destructive: true }); if (reason) declineReqM.mutate({ id: r.id, reason }); }} disabled={declineReqM.isPending}
                           className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg disabled:opacity-50">
                           Decline
                         </button>
                       </>
                     )}
                   </div>
                 </div>
               </li>
             ))}
           </ul>}
        </Card>
        <Card title="Documents" action={
          <>
            <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={onUpload} />
            <div className="flex gap-2">
              <button onClick={() => setRequestOpen(true)} className="text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-amber-200">
                Request from parent
              </button>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploadM.isPending} className="text-xs font-bold bg-[#0f172a] text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
                <Upload className="w-3.5 h-3.5" /> {uploadM.isPending ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </>
        }>
          {documents.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No documents uploaded.</p> :
            <ul className="divide-y divide-slate-100">
              {documents.map((d: any) => (
                <li key={d.id} className="py-3 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-semibold">{d.type ?? d.name ?? 'Document'}</p>
                      <p className="text-xs text-slate-500">Uploaded {dateOnly(d.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(d.url || d.file_url) ? (
                      <a
                        href={d.url ?? d.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-1 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg"
                      >
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400 italic">No preview</span>
                    )}
                    <button
                      onClick={async () => { if (await askConfirm('Delete this document permanently?')) deleteDocM.mutate(d.id); }}
                      disabled={deleteDocM.isPending}
                      className="text-xs font-semibold text-red-600 hover:underline flex items-center gap-1 bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg disabled:opacity-50"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          }
        </Card>
        </div>
      )}

      {/* EDIT MODAL */}
      {editOpen && (
        <Modal title="Edit student" onClose={() => setEditOpen(false)}>
          <form onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const data: Record<string, any> = {
              first_name: fd.get('first_name'),
              middle_name: fd.get('middle_name') || null,
              last_name: fd.get('last_name'),
              gender: fd.get('gender'),
              date_of_birth: fd.get('date_of_birth') || null,
              admitted_on: fd.get('admitted_on') || null,
              blood_group: fd.get('blood_group') || null,
              medical_notes: fd.get('medical_notes') || null,
              home_address: fd.get('home_address') || null,
              previous_school: fd.get('previous_school') || null,
              nationality: fd.get('nationality') || null,
              state_of_origin: fd.get('state_of_origin') || null,
              religion: fd.get('religion') || null,
              email: fd.get('email') || null,
              phone: fd.get('phone') || null,
            };
            const passportInput = e.currentTarget.querySelector<HTMLInputElement>('input[name="passport"]');
            const passport = passportInput?.files?.[0];
            saveProfileM.mutate({ data, passport });
          }} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-sm font-semibold mb-1">First name</label><input name="first_name" defaultValue={student.first_name} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              <div><label className="block text-sm font-semibold mb-1">Middle</label><input name="middle_name" defaultValue={student.middle_name ?? ''} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              <div><label className="block text-sm font-semibold mb-1">Surname</label><input name="last_name" defaultValue={student.last_name} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold mb-1">Gender</label>
                <select name="gender" defaultValue={student.gender} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm"><option value="male">Male</option><option value="female">Female</option></select>
              </div>
              <div><label className="block text-sm font-semibold mb-1">Date of birth</label><input name="date_of_birth" type="date" defaultValue={student.date_of_birth?.slice(0, 10)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold mb-1">Blood group</label><input name="blood_group" defaultValue={student.blood_group ?? ''} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              <div><label className="block text-sm font-semibold mb-1">Medical notes</label><input name="medical_notes" defaultValue={student.medical_notes ?? ''} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setEditOpen(false)} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={saveProfileM.isPending} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">{saveProfileM.isPending ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* WITHDRAW MODAL */}
      {withdrawOpen && (
        <Modal title="Withdraw student" onClose={() => setWithdrawOpen(false)}>
          <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); withdrawM.mutate({ reason: fd.get('reason') }); }} className="space-y-3">
            <p className="text-sm text-slate-600">Deactivates the account and stops billing. Can be reinstated later.</p>
            <div><label className="block text-sm font-semibold mb-1">Reason (required)</label><textarea name="reason" required rows={3} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setWithdrawOpen(false)} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={withdrawM.isPending} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50">{withdrawM.isPending ? 'Withdrawing…' : 'Withdraw'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* LINK PARENT MODAL */}
      {linkOpen && (
        <Modal title="Link parent" onClose={() => setLinkOpen(false)}>
          <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); linkM.mutate({ guardianId: Number(fd.get('guardian_id')), body: {
            student_id: studentId, relationship: fd.get('relationship'),
            is_primary: fd.get('is_primary') === 'on', can_pay: true,
          } }); }} className="space-y-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Existing guardian</label>
              <select name="guardian_id" required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                <option value="">Choose guardian…</option>
                {guardians.filter((g: any) => !guardiansList.some((eg: any) => eg.id === g.id)).map((g: any) => (
                  <option key={g.id} value={g.id}>{g.first_name} {g.last_name} · {g.phone}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Already-linked guardians are hidden. Create new ones on Families page.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Relationship</label>
              <select name="relationship" required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" name="is_primary" className="w-4 h-4" />
                <span className="font-semibold text-slate-800">Make this the primary payer</span>
              </label>
              <p className="text-xs text-slate-600 mt-1">Only one guardian per student can be the primary payer.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setLinkOpen(false)} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={linkM.isPending} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">{linkM.isPending ? 'Linking…' : 'Link parent'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* TRANSFER MODAL */}
      {reinstateOpen && student && (
        <ReinstateModal student={student} classes={classes} saving={reinstateM.isPending} onClose={() => setReinstateOpen(false)} onSave={(b: { school_class_id: number; password?: string }) => reinstateM.mutate(b)} />
      )}
      {transferOpen && (
        <Modal title="Transfer to another class" onClose={() => setTransferOpen(false)}>
          <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); transferM.mutate({ current_class_id: Number(fd.get('class_id')) }); }} className="space-y-3">
            <div><label className="block text-sm font-semibold mb-1">New class</label>
              <select name="class_id" required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                <option value="">Choose class…</option>
                {classes.filter((c: any) => c.id !== student.current_class_id).map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}{c.arm ? ` ${c.arm}` : ''}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setTransferOpen(false)} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={transferM.isPending} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">{transferM.isPending ? 'Transferring…' : 'Transfer'}</button>
            </div>
          </form>
        </Modal>
      )}

      {requestOpen && (
        <Modal title="Request document from parent" onClose={() => setRequestOpen(false)}>
          <form onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            requestDocM.mutate({ type: String(fd.get('type')), note: String(fd.get('note') || '') || undefined });
          }} className="space-y-3">
            <p className="text-sm text-slate-600">The parent/student will receive a notification asking them to upload the document.</p>
            <div>
              <label className="block text-sm font-semibold mb-1">Which document?</label>
              <select name="type" required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                <option value="birth_certificate">Birth certificate</option>
                <option value="passport_photo">Passport photograph</option>
                <option value="last_result">Last result slip</option>
                <option value="transfer_certificate">Transfer certificate</option>
                <option value="medical_record">Medical record</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Note (optional)</label>
              <textarea name="note" rows={3} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. Please provide a clear photo of the front page" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setRequestOpen(false)} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={requestDocM.isPending} className="flex-1 py-2.5 bg-amber-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50">
                {requestDocM.isPending ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Card({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
        <h3 className="font-bold text-slate-900 text-sm">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value, capitalize }: { label: string; value: string; capitalize?: boolean }) {
  return (
    <div className="py-2 border-b border-slate-100 last:border-b-0">
      <p className="text-xs font-semibold text-slate-500 uppercase">{label}</p>
      <p className={`text-sm text-slate-900 mt-0.5 ${capitalize ? 'capitalize' : ''}`}>{value}</p>
    </div>
  );
}

function QuickStat({ label, value, tone, action }: { label: string; value: string; tone?: string; action?: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg font-extrabold ${tone ?? 'text-slate-900'} font-mono break-all`}>{value}</p>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
